import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import util from 'preact-util';

@observer
class DyreIdReportBatchButton extends Component {
    constructor(props) {
        super(props);
        this.state = {
            reporting: false,
            results: null,
            error: null,
        };
    }

    countUnreported = () => {
        const { journals = [] } = this.props;
        let count = 0;
        for (const j of journals) {
            const diagnoses = j.diagnoses || [];
            for (const d of diagnoses) {
                if (d.uuidv4 && d.diagnoseCode && !d.dyreIdRegisteredId && !d.differential) {
                    count++;
                }
            }
        }
        return count;
    }

    countReported = () => {
        const { journals = [] } = this.props;
        let count = 0;
        for (const j of journals) {
            const diagnoses = j.diagnoses || [];
            for (const d of diagnoses) {
                if (d.dyreIdRegisteredId) {
                    count++;
                }
            }
        }
        return count;
    }

    handleBatchReport = async (e) => {
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }
        const { stores, saleId, journalId, customer, user, onReported } = this.props;

        // Pre-flight validation
        if (!customer?.dyreIdClinicId) {
            this.setState({ error: 'Clinic must be registered with DyreId' });
            return;
        }
        if (!user?.dyreIdVetId) {
            this.setState({ error: 'Veterinarian must be registered with DyreId' });
            return;
        }

        this.setState({ reporting: true, error: null, results: null });

        const { journalStore } = stores;
        // journalId (optional) limits the batch to one journal in the sale; the
        // journal form passes it, the Alle besøk column sends the whole visit.
        const data = await journalStore.reportAllDiagnosesToDyreId(saleId, journalId);

        if (data) {
            this.setState({
                reporting: false,
                results: data.results || [],
            });
            if (typeof onReported === 'function') {
                await onReported(data.results || []);
            }
        } else {
            this.setState({
                reporting: false,
                error: journalStore.dyreIdBatchReportError || 'Batch report failed',
            });
        }
    }

    render() {
        const { customer, buttonClass = 'btn btn-sm btn-outline-primary' } = this.props;
        const { reporting, results, error } = this.state;

        if (!customer?.featureFlags?.dyreid) {
            return null;
        }

        const unreportedCount = this.countUnreported();

        if (unreportedCount === 0 && !results && !reporting) {
            // All coded diagnoses are already reported: show a persistent
            // confirmation instead of a blank cell, so the state survives a
            // page reload (the in-memory `results` summary does not).
            const reportedCount = this.countReported();
            if (reportedCount > 0) {
                return (
                    <span class='small text-success'>
                        <i class='fa-solid fa-check me-1' />
                        <Text id='dyrejournal.dyreid-reported' fields={{ count: reportedCount }}>DyreId: {reportedCount} reported</Text>
                    </span>
                );
            }
            return null;
        }

        // Note: The spec mentions "Reporting 1 of 3..." progress, but since the batch
        // endpoint processes all diagnoses in a single HTTP request on the server side,
        // real-time per-item progress is not possible. We show a generic spinner instead.
        if (reporting) {
            return (
                <button type='button' class={buttonClass} disabled>
                    <i class='fa-solid fa-spinner fa-spin me-1' />
                    <Text id='dyrejournal.dyreid-sending' fields={{ count: unreportedCount }}>Sending {unreportedCount} to DyreID...</Text>
                </button>
            );
        }

        if (results) {
            const succeeded = results.filter(r => r.success).length;
            const failed = results.filter(r => !r.success).length;
            return (
                <div class='d-inline-flex flex-column'>
                    <span class={`small ${failed > 0 ? 'text-warning' : 'text-success'}`}>
                        <i class={`fa-solid ${failed > 0 ? 'fa-triangle-exclamation' : 'fa-check'} me-1`} />
                        DyreId: {succeeded} reported{failed > 0 ? `, ${failed} failed` : ''}
                    </span>
                    {failed > 0 && (
                        <div class='small text-danger'>
                            {results.filter(r => !r.success).map(r => (
                                <div key={r.diagnoseCode}>{r.diagnoseCode}: {r.errorMessage}</div>
                            ))}
                        </div>
                    )}
                </div>
            );
        }

        return (
            <span>
                <button
                    type='button'
                    class={buttonClass}
                    onClick={this.handleBatchReport}
                    title='Report all unreported diagnoses to DyreId/Pyramidion'
                >
                    <i class='fa-solid fa-upload me-1' />
                    <Text id='dyrejournal.dyreid-send' fields={{ count: unreportedCount }}>Send to DyreID ({unreportedCount})</Text>
                </button>
                {error && (
                    <div class='text-danger small mt-1'>
                        <i class='fa-solid fa-triangle-exclamation me-1' />
                        {error}
                    </div>
                )}
            </span>
        );
    }
}

export default DyreIdReportBatchButton;
